"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Session-vakt (BUG-2026-057, generaliserad på Eriks begäran): sessions-cookien
// delas mellan alla flikar. Loggar någon in som en annan användare (t.ex. som
// elev för att testa) i EN flik blir alla andra flikar tysta zombies — sidan
// ser ut som lärarens, men varje API-anrop svarar 401/403 och allt "slutar
// bara fungera" utan förklaring. Vakten gör bytet synligt överallt:
//
// 1. Varje sidladdning skriver sin serverrenderade identitet till en
//    localStorage-markör. Andra flikar får `storage`-eventet OMEDELBART och
//    jämför med sin egen identitet — skiljer de sig visas popupen innan
//    läraren ens hunnit klicka på något. (Login/logout behöver inga egna
//    krokar: nästa sidladdning efter bytet skriver markören.)
// 2. Backstop: en fetch-vakt fångar 401/403 från /api/* — täcker flikar som
//    missade storage-eventet (t.ex. väckt ur bfcache) och utloggad session.
//
// Lärar-impersonation ändrar INTE sessions-cookien (egen cookie, flik-scopad
// ?as=elev) och triggar därför inte vakten. Admins "logga in som" byter
// session på riktigt — då är admin-flikarna genuint stale och popupen korrekt.

const MARKOR = "vektr-session-identitet";

type Markor = { id: string | null; roll: string | null };

function lasMarkor(): Markor | null {
  try {
    const raw = localStorage.getItem(MARKOR);
    return raw ? (JSON.parse(raw) as Markor) : null;
  } catch {
    return null;
  }
}

export default function SessionVakt({
  sessionsId,
  roll,
}: {
  sessionsId: string | null;
  roll: string | null;
}) {
  const [varning, setVarning] = useState<null | {
    rubrik: string;
    text: string;
  }>(null);
  // En varning per "händelse" — nollställs inte förrän sidan laddas om, så
  // popupen inte staplas ovanpå sig själv av varje efterföljande 401.
  const harVarnat = useRef(false);

  useEffect(() => {
    // Utloggade sidor (login m.fl.) skriver ingen markör och vaktar inget —
    // men en inloggad sidas mount uppdaterar markören till sin identitet.
    const egen: Markor = { id: sessionsId, roll };
    try {
      const nuvarande = lasMarkor();
      if (sessionsId !== null && (!nuvarande || nuvarande.id !== sessionsId)) {
        localStorage.setItem(MARKOR, JSON.stringify(egen));
      }
      // Logout: markera aktivt att sessionen är borta så stale flikar varnas.
      if (sessionsId === null && nuvarande && nuvarande.id !== null) {
        localStorage.setItem(MARKOR, JSON.stringify({ id: null, roll: null }));
      }
    } catch {
      /* localStorage kan vara blockerad — vakten är då bara fetch-baserad */
    }

    function varnaByte(nyRoll: string | null) {
      if (harVarnat.current || sessionsId === null) return;
      harVarnat.current = true;
      setVarning(
        nyRoll === null
          ? {
              rubrik: "Du har loggats ut",
              text: "Kontot loggades ut i en annan flik. Den här sidan ser likadan ut men kan inte längre spara något — ladda om för att logga in igen.",
            }
          : {
              rubrik: "Inloggningen har bytts i en annan flik",
              text: `Någon loggade just in som en annan användare${nyRoll === "STUDENT" ? " (elev)" : ""} i en annan flik. Den här sidan visar fortfarande den förra användarens vy, men allt du gör sparas som den nya — eller nekas. Ladda om sidan för att fortsätta som rätt användare.`,
            },
      );
    }

    function onStorage(e: StorageEvent) {
      if (e.key !== MARKOR || sessionsId === null) return;
      let ny: Markor | null = null;
      try {
        ny = e.newValue ? (JSON.parse(e.newValue) as Markor) : null;
      } catch {
        return;
      }
      if (ny && ny.id !== sessionsId) varnaByte(ny.roll);
    }

    // Backstop: 401/403 från /api/* på en sida som renderades inloggad.
    const originalFetch = window.fetch;
    const vaktadFetch: typeof window.fetch = async (input, init) => {
      const res = await originalFetch(input, init);
      if (sessionsId !== null && (res.status === 401 || res.status === 403)) {
        const url =
          typeof input === "string"
            ? input
            : input instanceof URL
              ? input.href
              : input.url;
        if (url.includes("/api/")) {
          const markor = lasMarkor();
          if (!markor || markor.id !== sessionsId) varnaByte(markor?.roll ?? null);
        }
      }
      return res;
    };
    window.fetch = vaktadFetch;

    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      // Återställ bara om ingen annan hunnit wrappa ovanpå oss.
      if (window.fetch === vaktadFetch) window.fetch = originalFetch;
    };
  }, [sessionsId, roll]);

  if (!varning) return null;
  return createPortal(
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="sessionvakt-rubrik"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 shadow-xl">
        <h2
          id="sessionvakt-rubrik"
          className="mb-2 text-base font-semibold text-[var(--color-text)]"
        >
          {varning.rubrik}
        </h2>
        <p className="mb-4 text-sm leading-relaxed text-[var(--color-text)]">
          {varning.text}
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            title="Stäng varningen utan att ladda om — sidan förblir i otakt med inloggningen"
            onClick={() => setVarning(null)}
            className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-text-muted)] hover:bg-[var(--color-bg)]"
          >
            Ignorera
          </button>
          <button
            type="button"
            title="Ladda om sidan så att den följer den nu inloggade användaren"
            onClick={() => window.location.reload()}
            className="rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-sm text-white hover:opacity-90"
          >
            Ladda om sidan
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
